'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { PANEL_WIDTH_MIN, getMaxPanelWidth, clampPanelWidth, PANEL_VIEWPORT_MARGIN } from '@/lib/panel-resize';

const KEYBOARD_STEP = 16;
const KEYBOARD_BIG_STEP = 80;

export interface PanelResizeHandleProps {
  value: number; onWidthChange: (width: number) => void; ariaLabel: string; min?: number; max?: number;
  /** Gap from the viewport right edge when the panel is inset (compact floating mode). */
  rightInset?: number;
  /**
   * When false the handle becomes a pointer-only affordance: no tab stop and
   * hidden from assistive tech. Used by the embeddable chat widget, where an
   * extra "resize" stop is noise and width is adjusted via mouse/touch only.
   */
  keyboardResizable?: boolean;
  /** Override for aria-valuetext; defaults to English "{n} pixels wide…". */
  formatValueText?: (value: number) => string;
}

export default function PanelResizeHandle({
  value,
  onWidthChange,
  ariaLabel,
  min = PANEL_WIDTH_MIN,
  max = getMaxPanelWidth(),
  keyboardResizable = true,
  rightInset = 0,
  formatValueText,
}: PanelResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const effectiveMax = Math.min(max, typeof window !== 'undefined' ? window.innerWidth - PANEL_VIEWPORT_MARGIN : max);
  const effectiveMin = Math.min(min, effectiveMax);
  const clampedValue = clampPanelWidth(value, effectiveMax);
  const clearDragStyles = useCallback(() => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const applyWidth = useCallback((raw: number) => {
    const w = Math.round(Math.max(effectiveMin, Math.min(effectiveMax, raw)));
    onWidthChange(w);
  }, [effectiveMin, effectiveMax, onWidthChange]);
  const handlePointerMove = useCallback((clientX: number) => {
    if (typeof window === 'undefined') return;
    applyWidth(window.innerWidth - clientX - rightInset);
  }, [applyWidth, rightInset]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handlePointerMove(e.clientX);
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      clearDragStyles();
    };
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      handlePointerMove(touch.clientX);
    };
    const handleTouchEnd = () => {
      setIsDragging(false);
      clearDragStyles();
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      clearDragStyles();
    };
  }, [isDragging, handlePointerMove, clearDragStyles]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleTouchStart = () => setIsDragging(true);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? KEYBOARD_BIG_STEP : KEYBOARD_STEP;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      applyWidth(clampedValue + step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      applyWidth(clampedValue - step);
    }
  };
  return (
    <Box
      {...(keyboardResizable
        ? {
            role: 'separator',
            tabIndex: 0,
            'aria-orientation': 'vertical' as const,
            'aria-label': ariaLabel,
            'aria-valuenow': clampedValue,
            'aria-valuemin': effectiveMin,
            'aria-valuemax': effectiveMax,
            'aria-valuetext': formatValueText
              ? formatValueText(clampedValue)
              : `${clampedValue} pixels wide, use arrow keys to resize`,
          }
        : { 'aria-hidden': true })}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'absolute',
        left: -2,
        top: 0,
        bottom: 0,
        width: 4,
        cursor: 'col-resize',
        zIndex: 20,
        '&:focus': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        '&:hover > .panel-resize-strip, &:focus > .panel-resize-strip': {
          bgcolor: 'primary.main',
          opacity: 0.5,
        },
      }}
    >
      <Box
        className="panel-resize-strip"
        sx={{
          position: 'absolute',
          left: 2,
          top: 0,
          bottom: 0,
          width: 1,
          bgcolor: isDragging ? 'primary.main' : 'divider',
          opacity: isDragging ? 1 : 0,
          transition: 'background-color 0.2s, opacity 0.2s',
        }}
      />
    </Box>
  );
}
